import clsx from 'clsx'

import { useEffect, useRef } from 'react'
import { CaretDown, CaretRight } from 'phosphor-react'

import { DeleteCommentModal } from '../DeleteCommentModal'
import { type TextareaSuggestableActions } from '../TextareaSuggestable'
import { useComments } from './useComments'
import { useAuth } from '../../../../../../contexts/AuthContext'
import { Medias } from '../../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../../hooks/useMediaQuery'
import { MobileScreen } from '../../MobileScreen'
import { Comment } from './components/Comment'
import { CommentForm } from './components/CommentForm'

type CommentsContainerProps = {
  taskId: number
  isOpen: boolean
  onToggleOpen: () => void
}

export const CommentsContainer = ({
  taskId,
  isOpen,
  onToggleOpen,
}: CommentsContainerProps) => {
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)

  const commentsContainerRef = useRef<HTMLDivElement | null>(null)
  const textareaSuggestableMethodsRef =
    useRef<TextareaSuggestableActions | null>(null)

  const { user } = useAuth()
  const {
    comments,
    selectedComment,
    addCommentMutation,
    deleteCommentMutation,
    commentToDelete,
    updateCommentMutation,
    handleAddComment,
    handleCancelCommentEdition,
    handleCloseDeleteCommentModal,
    handleDeleteButtonClick,
    handleDeleteComment,
    handleSelectCommentToEdit,
    isDeleteCommentModalOpen,
  } = useComments(taskId, user!, textareaSuggestableMethodsRef)

  useEffect(() => {
    const commentContainerEl = commentsContainerRef.current

    if (commentContainerEl && isOpen) {
      commentContainerEl.scrollTo({
        behavior: 'smooth',
        top: commentContainerEl.scrollHeight,
      })
    }
  }, [comments, commentsContainerRef, isOpen])

  return (
    <>
      <div className="flex flex-col gap-4 mt-6 border-[1px] border-solid border-grey-200 rounded-lg shadow-[0px_2px_2px_rgba(0,0,0,0.12)]">
        <button
          type="button"
          className="flex items-center justify-between px-4 py-5"
          onClick={onToggleOpen}
        >
          <span className="text-sm font-medium ">Comentários</span>

          {isMinScreenWidthLG ? (
            <CaretDown
              className={clsx('transition transform ease-in-out', {
                'rotate-180': isOpen,
              })}
            />
          ) : (
            <CaretRight />
          )}
        </button>

        {!isMinScreenWidthLG ? (
          <MobileScreen
            title="Comentários"
            isVisible={isOpen}
            onGoBack={onToggleOpen}
          >
            <div className="flex flex-col grow">
              <div className="relative p-4 grow">
                {comments?.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <p className="font-sans text-sm text-grey-500">
                      Nenhum comentário na tarefa
                    </p>
                  </div>
                )}

                {comments?.length > 0 && (
                  <div
                    ref={commentsContainerRef}
                    className="max-h-[45vh] overflow-y-scroll"
                  >
                    {comments.map((comment, index) => {
                      return (
                        <Comment
                          key={comment.id}
                          hasBorderTop={index > 0}
                          comment={comment}
                          isSelected={comment.id === selectedComment?.id}
                          isActionsVisible={user!.id === comment.author.id}
                          onDeleteComment={handleDeleteButtonClick}
                          onEditComment={handleSelectCommentToEdit}
                        />
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-t-grey-500 pb-[26px] px-6 pt-4">
                <CommentForm
                  onCancel={handleCancelCommentEdition}
                  selectedComment={selectedComment}
                  onSubmit={handleAddComment}
                  textareaSuggestableMethodsRef={textareaSuggestableMethodsRef}
                  isSubmitButtonDisabled={!taskId}
                  isSubmitting={
                    addCommentMutation.isLoading ||
                    updateCommentMutation.isLoading
                  }
                />
              </div>
            </div>
          </MobileScreen>
        ) : (
          <>
            {isOpen && (
              <div className="px-4 pb-5 mt-4">
                <div className="relative py-5 pr-4 mb-8 rounded-lg border-1 border-grey-500 placeholder:text-grey-500">
                  {comments?.length === 0 && (
                    <p className="ml-4 font-sans text-sm text-grey-500">
                      Nenhum comentário na tarefa
                    </p>
                  )}

                  {comments.length > 0 && (
                    <div
                      ref={commentsContainerRef}
                      className="max-h-[300px] overflow-y-scroll pr-2 scrollbar"
                    >
                      {comments.map((comment, index) => {
                        return (
                          <Comment
                            key={comment.id}
                            hasBorderTop={index > 0}
                            comment={comment}
                            isSelected={comment.id === selectedComment?.id}
                            isActionsVisible={user!.id === comment.author.id}
                            onDeleteComment={handleDeleteButtonClick}
                            onEditComment={handleSelectCommentToEdit}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>

                <CommentForm
                  onCancel={handleCancelCommentEdition}
                  selectedComment={selectedComment}
                  onSubmit={handleAddComment}
                  textareaSuggestableMethodsRef={textareaSuggestableMethodsRef}
                  isSubmitButtonDisabled={!taskId}
                  isSubmitting={
                    addCommentMutation.isLoading ||
                    updateCommentMutation.isLoading
                  }
                />
              </div>
            )}
          </>
        )}
      </div>

      <DeleteCommentModal
        isRemoving={deleteCommentMutation.isLoading}
        commentToDelete={commentToDelete}
        isOpen={isDeleteCommentModalOpen}
        onClose={handleCloseDeleteCommentModal}
        onDelete={handleDeleteComment}
      />
    </>
  )
}
